/**
 * The masthead's "⋯" menu: Focus mode and Theme, the controls a reader reaches for rarely, so the bar itself holds only what is used
 * every session (menu, Salesforce, status, History, New session, profile).
 *
 * Activity (background syncs and scans) is not a menu item: its dropdown is its own popover, and a popover inside a popover clips.
 * It shows beside the "⋯" only while something is running or waiting, which is the only time it has anything to say.
 */
import { Maximize2, Minimize2, Monitor, Moon, MoreHorizontal, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useShallow } from "zustand/react/shallow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ncoder/ui";
import { IconButton } from "@/components/atoms/multi-agent/Button";
import { ActivityDropdown } from "@/components/molecules/ActivityDropdown";
import { useActivityStore } from "@/stores/activityStore";

export const OVERFLOW_LABELS = { trigger: "More", focusOn: "Focus mode", focusOff: "Exit focus mode", theme: "Theme" } as const;

/** Activity beside the "⋯" only while some process is not yet completed. */
export function MastheadActivity() {
  const activities = useActivityStore(useShallow((s) => s.activities));
  const any = Array.from(activities.values()).some((a) => a.status !== "completed");
  return any ? <ActivityDropdown /> : null;
}

export function MastheadOverflow({ isFocusMode, onToggleFocus }: { isFocusMode: boolean; onToggleFocus: () => void }) {
  const { setTheme } = useTheme();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton type="button" title={OVERFLOW_LABELS.trigger} aria-label={OVERFLOW_LABELS.trigger}>
          <MoreHorizontal className="h-4 w-4" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        <DropdownMenuItem onClick={onToggleFocus} className="flex items-center gap-2">
          {isFocusMode ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          {isFocusMode ? OVERFLOW_LABELS.focusOff : OVERFLOW_LABELS.focusOn}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">{OVERFLOW_LABELS.theme}</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => setTheme("light")} className="flex items-center gap-2">
          <Sun className="h-4 w-4" />
          Light
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("dark")} className="flex items-center gap-2">
          <Moon className="h-4 w-4" />
          Dark
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setTheme("system")} className="flex items-center gap-2">
          <Monitor className="h-4 w-4" />
          System
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
